import classNames from 'classnames';
import React from 'react';
import { Checkbox } from '../ui/Checkbox';

interface ActionListItem {
  id: string | number;
  title?: string;
  [key: string]: any;
}
interface ActionListProps {
  data?: ActionListItem[];
  actions?: (item: ActionListItem) => React.ReactNode;
  selected?: ActionListItem | null;
  selectedIds?: (string | number)[];
  showCheckbox?: boolean;
  onSelect: (item: ActionListItem, event?: React.MouseEvent) => void;
  isDisabled?: (item: ActionListItem) => boolean;
  disabledLabel?: string;
  className?: string;
  onListKeyDown?: React.KeyboardEventHandler<HTMLUListElement>;
}

const ActionList: React.FC<ActionListProps> = ({
  data,
  actions,
  selected,
  selectedIds,
  showCheckbox,
  onSelect,
  isDisabled,
  disabledLabel,
  className,
  onListKeyDown,
}) => {
  if (!data) return null;
  const ids = new Set(selectedIds?.map(String));
  return (
    <ul
      className={classNames('h-[320px] overflow-y-auto p-1', className)}
      onKeyDown={onListKeyDown}
    >
      {data.filter(Boolean).map((item) => {
        const checked =
          (selected != null && String(selected.id) === String(item.id)) || ids.has(String(item.id));
        const disabled = isDisabled?.(item) || false;
        return (
          <li
            key={item.id}
            data-testid={`action-list-item-${item.id}`}
            className={classNames(
              'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-neutral-700 transition-colors',
              {
                'cursor-pointer hover:bg-primary-50': !disabled,
                'bg-primary-50 text-primary-700': checked && showCheckbox,
                'bg-primary-600 text-white hover:bg-primary-600': checked && !showCheckbox,
                'opacity-50': disabled,
              }
            )}
            onClick={(event) => {
              if (!disabled) onSelect(item, event);
            }}
          >
            {showCheckbox ? (
              <>
                <Checkbox
                  aria-label={item.title || String(item.id)}
                  data-picker-checkbox="true"
                  checked={checked}
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!disabled) onSelect(item, event);
                  }}
                />
                <span className="min-w-0 grow break-words">{item.title || ''}</span>
              </>
            ) : (
              <button
                type="button"
                data-picker-checkbox="true"
                disabled={disabled}
                className="min-w-0 grow text-left break-words rounded focus-visible:ring-2"
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(item, event);
                }}
              >
                {item.title || ''}
              </button>
            )}
            {disabled && disabledLabel && <span className="shrink-0 text-xs">{disabledLabel}</span>}
            {actions && (
              <span className="shrink-0" onClick={(event) => event.stopPropagation()}>
                {actions(item)}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
export default ActionList;
